import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { getEasingForPreset } from '@/animation/easing/easingRegistry'
import type { AnimationClip, ClipKeyframe } from '@/types/animationClip'
import type { Vec3 } from '@/types/vec3'

export type LayerPose = {
  position: Vec3
  rotationDeg: Vec3
  scale: Vec3
  /** 0..1；非图片平面或未设置关键帧时按 1 */
  materialOpacity: number
}

function cloneVec3(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z }
}

function opacityFromKeyframe(k: ClipKeyframe): number {
  return clampMaterialOpacity(k.materialOpacity)
}

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u
}

function lerpVec3(a: Vec3, b: Vec3, u: number): Vec3 {
  return {
    x: lerp(a.x, b.x, u),
    y: lerp(a.y, b.y, u),
    z: lerp(a.z, b.z, u),
  }
}

/**
 * 在 clip 内对端点关键帧插值：先算段内线性的 `u`，再按 `clip.easingPreset` 缓动映射后 lerp；无关键帧时用 `fallback`。
 *
 * @param clip - 动画片段
 * @param localSeconds - 相对 clip 起点的时间（秒）
 * @param fallback - 无关键帧或越界时的基准姿态
 */
export function interpolateClipKeyframes(
  clip: AnimationClip,
  localSeconds: number,
  fallback: LayerPose,
): LayerPose {
  const keyframes = clip.keyframes
  const t = Number(localSeconds)
  const sorted = [...keyframes].sort((a, b) => a.timeInClip - b.timeInClip)

  if (sorted.length === 0) {
    return {
      position: cloneVec3(fallback.position),
      rotationDeg: cloneVec3(fallback.rotationDeg),
      scale: cloneVec3(fallback.scale),
      materialOpacity: clampMaterialOpacity(fallback.materialOpacity),
    }
  }

  if (sorted.length === 1) {
    const k = sorted[0]!
    return {
      position: cloneVec3(k.position),
      rotationDeg: cloneVec3(k.rotationDeg),
      scale: cloneVec3(k.scale),
      materialOpacity: opacityFromKeyframe(k),
    }
  }

  const first = sorted[0]!
  const last = sorted[sorted.length - 1]!

  if (t <= first.timeInClip) {
    return {
      position: cloneVec3(first.position),
      rotationDeg: cloneVec3(first.rotationDeg),
      scale: cloneVec3(first.scale),
      materialOpacity: opacityFromKeyframe(first),
    }
  }

  if (t >= last.timeInClip) {
    return {
      position: cloneVec3(last.position),
      rotationDeg: cloneVec3(last.rotationDeg),
      scale: cloneVec3(last.scale),
      materialOpacity: opacityFromKeyframe(last),
    }
  }

  let lo = 0
  let hi = sorted.length - 1
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2)
    if (sorted[mid]!.timeInClip <= t) lo = mid
    else hi = mid
  }

  const a = sorted[lo]!
  const b = sorted[hi]!
  const span = b.timeInClip - a.timeInClip
  const uLinear = span < 1e-12 ? 0 : (t - a.timeInClip) / span
  const easeFn = getEasingForPreset(clip.easingPreset)
  const u = easeFn(uLinear)

  return {
    position: lerpVec3(a.position, b.position, u),
    rotationDeg: lerpVec3(a.rotationDeg, b.rotationDeg, u),
    scale: lerpVec3(a.scale, b.scale, u),
    materialOpacity: lerp(opacityFromKeyframe(a), opacityFromKeyframe(b), u),
  }
}
